import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import NotFound404 from '../../ErrorPageComponent/404';
import * as Yup from 'yup';
import FormSkeleton from './FormSkeleton';
import ErrorState from './../../ErrorState/ErrorState';
import { useCachedFetch } from '../../../hooks/useCachedFetch';
import { AnimatedOnScroll } from '../../AnimatedOnScroll';
import { PROMO_API } from '../../../config/api';
import { getRecaptchaToken } from '../../../utils/recaptcha';
import { useConsent } from '../../../hooks/useConsent';
import { triggerShowBanner } from '../../CookieBanner/CookieBanner';
import './FormPromo.scss';

const FormPromo = ({ promoCode, discount, serviceName, hideHeaderFooterSetter }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;

    const {
        data: mainData,
        loading: mainLoading,
        error: mainError,
        refetch: refetchMainData
    } = useCachedFetch('/data/locales/mainData.json');

    const {
        data: discountData,
        loading: discountLoading,
        error: discountError,
        refetch: refetchDiscountData
    } = useCachedFetch('/data/locales/discount.json');

    const { hasConsentForForms } = useConsent();

    const handleRetry = useCallback(() => {
        refetchMainData();
        refetchDiscountData();
    }, [refetchMainData, refetchDiscountData]);

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isDataVerified, setIsDataVerified] = useState(false);

    const [emailError, setEmailError] = useState('');
    const [phoneFocused, setPhoneFocused] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isFading, setIsFading] = useState(false);
    const [isValidPromo, setIsValidPromo] = useState(true);
    const [formKey, setFormKey] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const formikRef = useRef(null);
    const fileInputRef = useRef(null);

    const formPromoData = useMemo(() => {
        if (!mainData || !discountData) return null;
        return {
            formData: mainData.TopMainData?.FormSection,
            discountData: discountData.DiscountData
        };
    }, [mainData, discountData]);

    useEffect(() => {
        if (mainLoading || discountLoading) return;

        if (mainError || discountError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        if (formPromoData?.formData && formPromoData?.discountData) {
            setHasError(false);
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [mainLoading, discountLoading, mainError, discountError, formPromoData]);

    const currentTranslations = useMemo(() => {
        return formPromoData?.formData?.[currentLang] || formPromoData?.formData?.en || {};
    }, [formPromoData, currentLang]);

    const errorMessages = useMemo(() => ({
        en: {
            nameRequired: "The field is required",
            nameInvalid: "Only letters and spaces are allowed",
            emailInvalid: "Invalid email address",
            emailDomain: "Email must contain a valid domain (e.g. gmail.com)",
            emailRequired: "The field is required",
            phoneInvalid: "Phone number must be between 10 and 12 digits",
            phoneRequired: "The field is required",
            keyboardLanguage: "Switch your keyboard",
            descriptionPlaceholder: "The more we know, the better.",
            submitting: "Sending...",
            messageTooLong: "Message must not exceed 2000 characters",
            fileTooLarge: "File must not exceed 5 MB",
            fileInvalidType: "Unsupported file type. Allowed: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Submission error. Please try again.",
            rateLimitError: "Too many requests. Please try again later.",
            consentError: "Consent to personal data processing is required to submit the form",
        },
        ua: {
            nameRequired: "Поле обов'язкове для заповнення",
            nameInvalid: "Дозволені лише літери та пробіли",
            emailInvalid: "Невірна email адреса",
            emailDomain: "Email повинен містити дійсний домен (наприклад, gmail.com)",
            emailRequired: "Поле обов'язкове для заповнення",
            phoneInvalid: "Номер телефону повинен містити від 10 до 12 цифр",
            phoneRequired: "Поле обов'язкове для заповнення",
            keyboardLanguage: "Змініть мову на клавіатурі",
            descriptionPlaceholder: "Чим більше ми знаємо, тим краще.",
            submitting: "Відправка...",
            messageTooLong: "Повідомлення не повинно перевищувати 2000 символів",
            fileTooLarge: "Файл не повинен перевищувати 5 МБ",
            fileInvalidType: "Непідтримуваний тип файлу. Дозволені: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Помилка відправки. Спробуйте ще раз.",
            rateLimitError: "Занадто багато запитів. Спробуйте пізніше.",
            consentError: "Для відправки форми необхідна згода на обробку персональних даних",
        },
        ru: {
            nameRequired: "Поле обязательно для заполнения",
            nameInvalid: "Разрешены только буквы и пробелы",
            emailInvalid: "Неверный адрес электронной почты",
            emailDomain: "Email должен содержать действительный домен (например, gmail.com)",
            emailRequired: "Поле обязательно для заполнения",
            phoneInvalid: "Номер телефона должен содержать от 10 до 12 цифр",
            phoneRequired: "Поле обязательно для заполнения",
            keyboardLanguage: "Смените язык на клавиатуре",
            descriptionPlaceholder: "Чем больше мы знаем, тем лучше.",
            submitting: "Отправка...",
            messageTooLong: "Сообщение не должно превышать 2000 символов",
            fileTooLarge: "Файл не должен превышать 5 МБ",
            fileInvalidType: "Неподдерживаемый тип файла. Разрешены: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Ошибка отправки. Попробуйте еще раз.",
            rateLimitError: "Слишком много запросов. Попробуйте позже.",
            consentError: "Для отправки формы необходимо согласие на обработку персональных данных",
        },
        pl: {
            nameRequired: "Pole jest wymagane",
            nameInvalid: "Dozwolone są tylko litery i spacje",
            emailInvalid: "Nieprawidłowy adres email",
            emailDomain: "Email musi zawierać prawidłową domenę (np. gmail.com)",
            emailRequired: "Pole jest wymagane",
            phoneInvalid: "Numer telefonu musi zawierać od 10 do 12 cyfr",
            phoneRequired: "Pole jest wymagane",
            keyboardLanguage: "Zmień język klawiatury",
            descriptionPlaceholder: "Im więcej wiemy, tym lepiej.",
            submitting: "Wysyłanie...",
            messageTooLong: "Wiadomość nie może przekraczać 2000 znaków",
            fileTooLarge: "Plik nie może przekraczać 5 MB",
            fileInvalidType: "Nieobsługiwany typ pliku. Dozwolone: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Błąd wysyłania. Spróbuj ponownie.",
            rateLimitError: "Zbyt wiele żądań. Spróbuj później.",
            consentError: "Do wysłania formularza wymagana jest zgoda na przetwarzanie danych osobowych",
        },
        de: {
            nameRequired: "Feld ist erforderlich",
            nameInvalid: "Nur Buchstaben und Leerzeichen sind erlaubt",
            emailInvalid: "Ungültige E-Mail-Adresse",
            emailDomain: "E-Mail muss eine gültige Domain enthalten (z.B. gmail.com)",
            emailRequired: "Feld ist erforderlich",
            phoneInvalid: "Telefonnummer muss zwischen 10 und 12 Ziffern enthalten",
            phoneRequired: "Feld ist erforderlich",
            keyboardLanguage: "Tastatursprache wechseln",
            descriptionPlaceholder: "Je mehr wir wissen, desto besser.",
            submitting: "Senden...",
            messageTooLong: "Nachricht darf 2000 Zeichen nicht überschreiten",
            fileTooLarge: "Datei darf 5 MB nicht überschreiten",
            fileInvalidType: "Nicht unterstützter Dateityp. Erlaubt: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Fehler beim Senden. Bitte versuchen Sie es erneut.",
            rateLimitError: "Zu viele Anfragen. Bitte versuchen Sie es später erneut.",
            consentError: "Für das Absenden des Formulars ist die Zustimmung zur Verarbeitung personenbezogener Daten erforderlich",
        },
        es: {
            nameRequired: "El campo es obligatorio",
            nameInvalid: "Solo se permiten letras y espacios",
            emailInvalid: "Dirección de correo electrónico no válida",
            emailDomain: "El correo electrónico debe contener un dominio válido (por ejemplo, gmail.com)",
            emailRequired: "El campo es obligatorio",
            phoneInvalid: "El número de teléfono debe tener entre 10 y 12 dígitos",
            phoneRequired: "El campo es obligatorio",
            keyboardLanguage: "Cambiar idioma del teclado",
            descriptionPlaceholder: "Cuanto más sepamos, mejor.",
            submitting: "Enviando...",
            messageTooLong: "El mensaje no debe exceder los 2000 caracteres",
            fileTooLarge: "El archivo no debe exceder los 5 MB",
            fileInvalidType: "Tipo de archivo no soportado. Permitidos: JPG, PNG, GIF, PDF, DOC, DOCX, TXT",
            subscribeError: "Error al enviar. Por favor, inténtelo de nuevo.",
            rateLimitError: "Demasiadas solicitudes. Por favor, inténtelo más tarde.",
            consentError: "Para enviar el formulario se requiere el consentimiento para el procesamiento de datos personales",
        }
    }), []);

    const currentErrors = useMemo(() =>
        errorMessages[currentLang] || errorMessages.en,
        [currentLang, errorMessages]
    );

    useEffect(() => {
        setEmailError("");
        setFormSubmitted(false);
        setIsFading(false);
        setFormKey(prev => prev + 1);
    }, [currentLang]);

    useEffect(() => {
        if (!formPromoData) return;

        const validPromoCodes = formPromoData?.discountData?.offers?.flatMap(offer =>
            Object.values(offer.translations || {}).map(t => t.promocode)
        ).filter(Boolean) || [];

        const normalizedPromoCode = promoCode?.trim().toUpperCase();
        const normalizedValidCodes = validPromoCodes.map(code => code?.trim().toUpperCase());

        const isValid = normalizedValidCodes.includes(normalizedPromoCode);

        setIsValidPromo(isValid);
        setIsDataVerified(true);
    }, [promoCode, formPromoData]);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.setFieldValue('promocode', promoCode);
        }
    }, [promoCode]);

    useEffect(() => {
        if (hideHeaderFooterSetter) hideHeaderFooterSetter(!isValidPromo && isDataVerified);
        return () => hideHeaderFooterSetter && hideHeaderFooterSetter(false);
    }, [isValidPromo, isDataVerified, hideHeaderFooterSetter]);

    const initialValues = useMemo(() => ({
        name: '',
        email: '',
        phone: '',
        description: '',
        file: null,
        promocode: promoCode,
        discount: discount || '',
        serviceName: serviceName || '',
    }), [promoCode, discount, serviceName]);

    const validationSchema = useMemo(() =>
        Yup.object({
            name: Yup.string()
                .matches(/^[A-Za-zА-Яа-яЁё\s]+$/, currentErrors.nameInvalid)
                .required(currentErrors.nameRequired),
            email: Yup.string()
                .email(currentErrors.emailInvalid)
                .matches(
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                    currentErrors.emailDomain
                )
                .required(currentErrors.emailRequired),
            phone: Yup.string()
                .matches(/^\+?\d{10,12}$/, currentErrors.phoneInvalid)
                .required(currentErrors.phoneRequired),
            description: Yup.string()
                .max(2000, currentErrors.messageTooLong)
                .nullable(),
            file: Yup.mixed()
                .nullable()
                .test('fileSize', currentErrors.fileTooLarge, (value) => {
                    if (!value) return true;
                    return value.size <= 5 * 1024 * 1024;
                })
                .test('fileType', currentErrors.fileInvalidType, (value) => {
                    if (!value) return true;
                    const allowedTypes = [
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                        'application/pdf',
                        'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'text/plain'
                    ];
                    return allowedTypes.includes(value.type);
                }),
            discount: Yup.string().optional(),
            serviceName: Yup.string().optional(),
        }),
        [currentErrors]
    );

    const formatPhoneNumber = useCallback((value) => {
        const cleaned = value.replace(/\D+/g, '');
        return cleaned && cleaned[0] !== '+' ? `+${cleaned}` : cleaned;
    }, []);

    const handleSubmit = useCallback(async (values, { resetForm }) => {
        const { discount, serviceName } = values;
        if (!hasConsentForForms()) {
            setSubmitError(currentErrors.consentError);
            setFormSubmitted(false);
            setIsSubmitting(false);
            triggerShowBanner();
            setTimeout(() => {
                setSubmitError('');
            }, 10000);
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');

        try {
            let recaptchaToken = '';
            try {
                recaptchaToken = await getRecaptchaToken('promo');
            } catch (err) {
                console.warn('reCAPTCHA error:', err);
            }

            const formData = new FormData();
            formData.append('name', values.name);
            formData.append('email', values.email);
            formData.append('phone', values.phone || '');
            formData.append('message', values.description || '');
            formData.append('promocode', values.promocode || '');
            formData.append('formType', 'promo');
            formData.append('recaptchaToken', recaptchaToken);
            formData.append('language', currentLang);
            formData.append('discount', discount); 
            formData.append('serviceName', serviceName);

            if (values.file) {
                formData.append('file', values.file);
            }

            const response = await fetch(PROMO_API, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (response.ok) {

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'form_lead_submitted',
                    form_name: 'promo_form'
                });

                setFormSubmitted(true);
                setSubmitError('');
                resetForm();
                if (fileInputRef.current) fileInputRef.current.value = '';

                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setIsSubmitting(false);
                    }, 1000);
                }, 3000);
            } else if (response.status === 429) {
                setSubmitError(currentErrors.rateLimitError);
                setFormSubmitted(true);
                setIsSubmitting(false);
                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setSubmitError('');
                    }, 1000);
                }, 3000);
            } else {
                const errorMessage = data.errors?.[0]?.msg || data.message || 'Ошибка отправки';
                setSubmitError(errorMessage);
                setFormSubmitted(true);
                setIsSubmitting(false);
                setTimeout(() => {
                    setIsFading(true);
                    setTimeout(() => {
                        setFormSubmitted(false);
                        setIsFading(false);
                        setSubmitError('');
                    }, 1000);
                }, 3000);
            }
        } catch (error) {
            console.error('Network error:', error);
            setSubmitError(currentErrors.subscribeError);
            setFormSubmitted(true);
            setIsSubmitting(false);
            setTimeout(() => {
                setIsFading(true);
                setTimeout(() => {
                    setFormSubmitted(false);
                    setIsFading(false);
                    setSubmitError('');
                }, 1000);
            }, 3000);
        }
    }, [currentErrors, hasConsentForForms, currentLang]);

    const handleNameInput = useCallback((e, setFieldTouched, setFieldError) => {
        if (e.target.value) {
            setFieldTouched('name', true, false);
            setFieldError('name', '');
        }
        e.target.value = e.target.value.replace(/[^A-Za-zА-Яа-яЁё\s]/g, '');
    }, []);

    const handleEmailInput = useCallback((e, setFieldTouched, setFieldError) => {
        const value = e.target.value;
        setEmailError(/[А-Яа-яЁё]/.test(value) ? currentErrors.keyboardLanguage : '');
        e.target.value = value.replace(/[^A-Za-z0-9@._-]/g, '');
        if (value) {
            setFieldTouched('email', true, false);
            setFieldError('email', '');
        }
    }, [currentErrors]);

    const handlePhoneChange = useCallback((e, setFieldValue, setFieldTouched, setFieldError) => {
        const formatted = formatPhoneNumber(e.target.value);
        if (formatted.length <= 13) setFieldValue('phone', formatted);
        if (formatted) {
            setFieldTouched('phone', true, false);
            setFieldError('phone', '');
        }
    }, [formatPhoneNumber]);

    const handleFileChange = useCallback((event, setFieldValue, setFieldError) => {
        const file = event.currentTarget.files[0];
        setFieldValue('file', file);
        setFieldError('file', '');
    }, []);

    const handleRemoveFile = useCallback((setFieldValue, setFieldError) => {
        setFieldValue('file', null);
        setFieldError('file', '');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    const getFileIcon = useCallback((fileType) => {
        if (!fileType) return '📎';
        if (fileType.startsWith('image/')) return '🖼️';
        if (fileType === 'application/pdf') return '📄';
        if (fileType.includes('word')) return '📝';
        if (fileType === 'text/plain') return '📃';
        return '📎';
    }, []);

    if (isLoading) {
        return <FormSkeleton />;
    }

    if (hasError || !formPromoData) {
        return <ErrorState onRetry={handleRetry} theme={'dark'} />;
    }

    if (!isValidPromo && isDataVerified) {
        return <NotFound404 hideHeaderFooterSetter={hideHeaderFooterSetter} />;
    }

    return (
        <Formik
            key={formKey}
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
        >
            {({ setFieldValue, values, handleSubmit, setFieldTouched, setFieldError }) => (
                <Form className="promo__form" onSubmit={handleSubmit}>
                    <div className="promo__form__row">
                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="promo__form__row__item">
                                <div className="promo__form__input__label">
                                    <Field
                                        name="name"
                                        type="text"
                                        id="name"
                                        placeholder=" "
                                        autoComplete="name"
                                        onInput={(e) => handleNameInput(e, setFieldTouched, setFieldError)}
                                    />
                                    <label htmlFor="name">{currentTranslations?.name}</label>
                                    <ErrorMessage name="name" component="div" className="promo__error__message" />
                                </div>
                                <div className="promo__form__input__label">
                                    <Field
                                        name="email"
                                        type="email"
                                        id="email"
                                        placeholder=" "
                                        autoComplete="email"
                                        onInput={(e) => handleEmailInput(e, setFieldTouched, setFieldError)}
                                    />
                                    <label htmlFor="email">{currentTranslations?.email}</label>
                                    {emailError && !values.email && (
                                        <div className="promo__switch__keyboard__error">{emailError}</div>
                                    )}
                                    <ErrorMessage name="email" component="div" className="promo__error__message" />
                                </div>
                            </div>
                        </AnimatedOnScroll>

                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="promo__form__row__item">
                                <div className="promo__form__input__label">
                                    <Field
                                        name="phone"
                                        type="text"
                                        id="phone"
                                        placeholder={phoneFocused || values.phone ? ' ' : '* Phone'}
                                        autoComplete="tel"
                                        value={values.phone}
                                        onFocus={() => {
                                            setPhoneFocused(true);
                                            if (!values.phone.startsWith('+')) setFieldValue('phone', `+${values.phone}`);
                                        }}
                                        onBlur={() => setPhoneFocused(false)}
                                        onChange={(e) => handlePhoneChange(e, setFieldValue, setFieldTouched, setFieldError)}
                                    />
                                    <label htmlFor="phone">{currentTranslations?.phone}</label>
                                    <ErrorMessage name="phone" component="div" className="promo__error__message" />
                                </div>
                                <div className="promo__form__input__label">
                                    <Field
                                        name="promocode"
                                        type="text"
                                        id="promocode"
                                        placeholder=" "
                                        autoComplete="off"
                                        value={promoCode}
                                        readOnly
                                    />
                                    <label htmlFor="promocode">{currentTranslations?.promocode}</label>
                                </div>
                            </div>
                        </AnimatedOnScroll>

                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="promo__form__row__item">
                                <div className="promo__form__textarea__label">
                                    <Field
                                        name="description"
                                        as="textarea"
                                        id="description"
                                        className="promo__form__textarea"
                                        placeholder={currentErrors.descriptionPlaceholder}
                                        autoComplete="off"
                                    />
                                    <label htmlFor="description">{currentTranslations?.textarea}</label>
                                    <ErrorMessage name="description" component="div" className="promo__error__message" />
                                </div>
                            </div>
                        </AnimatedOnScroll>

                        <AnimatedOnScroll animation="fade-up" delay={0}>
                            <div className="promo__form__row__item">
                                <div className="promo__form__file__input">
                                    <input
                                        name="file"
                                        type="file"
                                        id="file"
                                        ref={fileInputRef}
                                        onChange={(event) => handleFileChange(event, setFieldValue, setFieldError)}
                                        style={{ display: 'none' }}
                                    />
                                    <label htmlFor="file" className="promo__form__file__label">
                                        {currentTranslations?.attach}
                                    </label>
                                    {values.file && (
                                        <div className="promo__file__info">
                                            <span className="promo__file__name">
                                                {getFileIcon(values.file.type)} {values.file.name}
                                                <span className="promo__file__size"> ({formatFileSize(values.file.size)})</span>
                                            </span>
                                            <button
                                                type="button"
                                                className="promo__file__remove"
                                                onClick={() => handleRemoveFile(setFieldValue, setFieldError)}
                                                aria-label="Удалить файл"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}

                                    <ErrorMessage name="file" component="div" className="promo__error__message" />
                                    {submitError && submitError === currentErrors.consentError && (
                                        <div className="promo__error__message">
                                            <p>{submitError}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="promo__form__buttons">
                                    {formSubmitted ? (
                                        <div className={`promo__form__success__message ${isFading ? 'fade-out' : 'fade-in'}`}>
                                            <p>
                                                {submitError
                                                    ? submitError
                                                    : currentTranslations?.success}
                                            </p>
                                        </div>
                                    ) : (
                                        <button
                                            type="submit"
                                            className="promo__submit__button"
                                            disabled={isSubmitting}
                                        >
                                            <span></span><span></span><span></span><span></span>
                                            {isSubmitting ? currentErrors.submitting : currentTranslations?.submit}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </AnimatedOnScroll>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default React.memo(FormPromo);